/**
 * Content analysis data fetching hooks.
 * 
 * @module hooks/useContent
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type {
  ContentCheckerWithBusiness,
  FAQAuditWithBusiness,
  LLMsTxtGenerationWithBusiness,
  PaginatedResponse,
  ContentQueryOptions,
} from '../types';

/**
 * Fetches paginated content checker results.
 * 
 * @param options - Query options
 * @returns Query result with paginated content checker results
 */
export function useContentChecker(options: ContentQueryOptions = {}) {
  const {
    page = 1,
    pageSize = 25,
    businessId,
    sortBy = 'created_at',
    sortOrder = 'desc',
  } = options;

  return useQuery({
    queryKey: ['content-checker', options],
    queryFn: async (): Promise<PaginatedResponse<ContentCheckerWithBusiness>> => {
      let query = supabase
        .from('content_checker')
        .select('*, businesses(id, business_name, users!businesses_profile_id_fkey(email))', { count: 'exact' });

      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        throw new Error(`Failed to fetch content checker: ${error.message}`);
      }

      return {
        data: (data as ContentCheckerWithBusiness[]) || [],
        count: count || 0,
        page,
        pageSize,
        totalPages: Math.ceil((count || 0) / pageSize),
      };
    },
  });
}

/**
 * Fetches paginated FAQ audit results.
 * 
 * @param options - Query options
 * @returns Query result with paginated FAQ audits
 */
export function useFAQAudits(options: ContentQueryOptions = {}) {
  const {
    page = 1,
    pageSize = 25,
    businessId,
    sortBy = 'created_at',
    sortOrder = 'desc',
  } = options;

  return useQuery({
    queryKey: ['faq-audits', options],
    queryFn: async (): Promise<PaginatedResponse<FAQAuditWithBusiness>> => {
      let query = supabase
        .from('faq_audit')
        .select('*, businesses(id, business_name, users!businesses_profile_id_fkey(email))', { count: 'exact' });

      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        throw new Error(`Failed to fetch FAQ audits: ${error.message}`);
      }

      return {
        data: (data as FAQAuditWithBusiness[]) || [],
        count: count || 0,
        page,
        pageSize,
        totalPages: Math.ceil((count || 0) / pageSize),
      };
    },
  });
}

/**
 * Fetches paginated LLMs.txt generations.
 * 
 * @param options - Query options
 * @returns Query result with paginated LLMs.txt generations
 */
export function useLLMsTxt(options: ContentQueryOptions = {}) {
  const {
    page = 1,
    pageSize = 25,
    businessId,
    sortBy = 'created_at',
    sortOrder = 'desc',
  } = options;

  return useQuery({
    queryKey: ['llms-txt', options],
    queryFn: async (): Promise<PaginatedResponse<LLMsTxtGenerationWithBusiness>> => {
      let query = supabase
        .from('llms_txt_generations')
        .select('*, businesses(id, business_name, users!businesses_profile_id_fkey(email))', { count: 'exact' });

      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        throw new Error(`Failed to fetch LLMs.txt: ${error.message}`);
      }

      return {
        data: (data as LLMsTxtGenerationWithBusiness[]) || [],
        count: count || 0,
        page,
        pageSize,
        totalPages: Math.ceil((count || 0) / pageSize),
      };
    },
  });
}

/**
 * Fetches content checker for a specific business.
 * 
 * @param businessId - Business ID
 * @returns Query result with business content checker results
 */
export function useBusinessContentChecker(businessId: string | undefined) {
  return useQuery({
    queryKey: ['business-content-checker', businessId],
    queryFn: async (): Promise<ContentCheckerWithBusiness[]> => {
      if (!businessId) throw new Error('Business ID is required');

      const { data, error } = await supabase
        .from('content_checker')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch content checker: ${error.message}`);
      }

      return (data as ContentCheckerWithBusiness[]) || [];
    },
    enabled: !!businessId,
  });
}

/**
 * Fetches FAQ audits for a specific business.
 * 
 * @param businessId - Business ID
 * @returns Query result with business FAQ audits
 */
export function useBusinessFAQAudits(businessId: string | undefined) {
  return useQuery({
    queryKey: ['business-faq-audits', businessId],
    queryFn: async (): Promise<FAQAuditWithBusiness[]> => {
      if (!businessId) throw new Error('Business ID is required');

      const { data, error } = await supabase
        .from('faq_audit')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch FAQ audits: ${error.message}`);
      }

      return (data as FAQAuditWithBusiness[]) || [];
    },
    enabled: !!businessId,
  });
}

/**
 * Fetches LLMs.txt generations for a specific business.
 * 
 * @param businessId - Business ID
 * @returns Query result with business LLMs.txt generations
 */
export function useBusinessLLMsTxt(businessId: string | undefined) {
  return useQuery({
    queryKey: ['business-llms-txt', businessId],
    queryFn: async (): Promise<LLMsTxtGenerationWithBusiness[]> => {
      if (!businessId) throw new Error('Business ID is required');

      const { data, error } = await supabase
        .from('llms_txt_generations')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch LLMs.txt: ${error.message}`);
      }

      return (data as LLMsTxtGenerationWithBusiness[]) || [];
    },
    enabled: !!businessId,
  });
}
